import React, { useState } from 'react';
import { useUser } from '@/shared/hooks/useUser';
import { useNavigate } from 'react-router-dom';
import { logout } from '@/shared/api/authentication-api';
import { useToast } from '@/hooks/use-toast';

// Import shadcn/ui components
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface TopbarProps {
  onMenuClick: () => void;
}

const Topbar: React.FC<TopbarProps> = ({ onMenuClick }) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      // Clear localStorage token if it exists
      localStorage.removeItem('token');
      
      toast({
        title: "Success",
        description: "Logged out successfully",
        variant: 'default',
        className: 'bg-green-500 text-white',
        duration: 3000,
      });
      
      // Redirect to homepage and reload to clear user state
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Error",
        description: "Failed to logout. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
      setIsLoggingOut(false);
    }
  };

  // Mock banner data - replace with actual banner logic
  const activeBanner: { text: string } | null = null; // Set to banner object if active

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user?.first_name) return 'U';
    const firstInitial = user.first_name[0]?.toUpperCase() || '';
    const lastInitial = user.last_name?.[0]?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  return (
    <div className="sticky top-0 z-30 flex h-16 shrink-0 items-center justify-between border-b border-border dark:bg-[hsl(222,47%,4%)] bg-background px-4 shadow-sm sm:px-6 lg:px-8">
      {/* Left: Mobile menu + Logo */}
      <div className="flex items-center gap-x-4">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="-m-2.5 p-2.5 text-foreground lg:hidden"
          onClick={onMenuClick}
        >
          <span className="sr-only">Open sidebar</span>
          <svg
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </Button>

        {/* Logo */}
        <div className="flex items-center">
          <img
            className="h-8 w-auto dark:hidden"
            src="/static/images/iv360-p-500.png"
            alt="Ironview 360"
          />
          <img
            className="h-8 w-auto hidden dark:block"
            src="/static/images/iv360-p-500-white.png"
            alt="Ironview 360"
          />
        </div>
      </div>

      {/* Center: Banner notification (if active) */}
      <div className="flex-1 flex items-center justify-center px-4">
        {activeBanner && (
          <div className="flex items-center bg-blue-600 text-white px-4 py-2 rounded-lg">
            <span className="text-sm font-medium">{(activeBanner as any)?.text}</span>
            <Button
              variant="ghost"
              size="icon"
              className="ml-4 text-white hover:text-gray-200 h-4 w-4"
              onClick={() => {/* Handle banner close */}}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>
        )}
      </div>

      {/* Right: User profile dropdown */}
      <div className="flex items-center">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="-m-1.5 flex items-center p-1.5 hover:bg-accent rounded-lg transition-colors">
              <span className="sr-only">Open user menu</span>
              <Avatar className="h-8 w-8">
                <AvatarImage 
                  src={(user as any)?.photo || "/media/images/user_thumbnails/avatar.png"} 
                  alt="Profile"
                />
                <AvatarFallback className="bg-muted text-muted-foreground text-sm font-medium">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="hidden lg:flex lg:items-center lg:ml-3">
                <div className="text-sm">
                  <div className="font-medium text-foreground">
                    Welcome, {user?.first_name}!
                  </div>
                </div>
                <svg
                  className="ml-2 h-5 w-5 text-muted-foreground"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate('/app/account')}>
              My Account
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout} 
              disabled={isLoggingOut}
              className="text-red-600 focus:text-red-600"
            >
              {isLoggingOut ? 'Logging out...' : 'Log Out'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default Topbar; 
